import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * A chart from a data answer. The server copied every value from the query
 * result; this only draws them. No chart library: the widget ships inside other
 * products' pages and stays small, and three kinds cover what a chat answer
 * needs — a trend (line), a comparison (bar, drawn horizontally so long names
 * fit a narrow panel) and a share of a whole (pie, as a donut).
 */
export function DataChart({ block }) {
    const body = block.kind === 'line' ? _jsx(LineChart, { block: block }) : block.kind === 'pie' ? _jsx(PieChart, { block: block }) : _jsx(BarChart, { block: block });
    return (_jsxs("figure", { className: "ac-chart", "aria-label": block.title, children: [block.title && _jsx("figcaption", { className: "ac-chart-title", children: block.title }), body, block.series.length > 1 && block.kind !== 'pie' && (_jsx("div", { className: "ac-chart-legend", children: block.series.map((s, i) => (_jsxs("span", { children: [_jsx("i", { style: { background: color(i) } }), s.label] }, s.key))) }))] }));
}
const PALETTE = ['var(--ac-primary)', '#e8a33d', '#3fa796', '#c45a8a', '#6b7fd7', '#8a8f98', '#d9674c', '#57a0d3'];
function color(i) {
    return PALETTE[i % PALETTE.length];
}
function num(value) {
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}
const INDIAN = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 });
/** A value as a reader expects it: ₹1,48,84,861 · 12.66% · 242. */
export function formatValue(value, format, currency = '') {
    if (typeof value !== 'number')
        return value == null ? '' : String(value);
    if (format === 'pct')
        return `${INDIAN.format(value)}%`;
    if (format === 'label')
        return String(value);
    const text = INDIAN.format(value);
    return format === 'money' && currency ? (value < 0 ? `-${currency}${text.slice(1)}` : `${currency}${text}`) : text;
}
/** Short, for an axis: ₹1.49 Cr · ₹12.5 L · 4.2K. */
function compact(value, format, currency = '') {
    if (format === 'pct')
        return `${INDIAN.format(value)}%`;
    const abs = Math.abs(value);
    const sign = value < 0 ? '-' : '';
    const unit = abs >= 1e7 ? [1e7, ' Cr'] : abs >= 1e5 ? [1e5, ' L'] : abs >= 1e3 ? [1e3, 'K'] : [1, ''];
    const shown = `${sign}${Number((abs / unit[0]).toFixed(abs >= 1e3 ? 1 : 0))}${unit[1]}`;
    return format === 'money' && currency ? `${sign}${currency}${shown.replace('-', '')}` : shown;
}
function BarChart({ block }) {
    const max = Math.max(0, ...block.rows.flatMap((row) => row.slice(1).map(num)));
    const min = Math.min(0, ...block.rows.flatMap((row) => row.slice(1).map(num)));
    const span = max - min || 1;
    return (_jsx("div", { className: "ac-bars", role: "list", children: block.rows.map((row, i) => (_jsxs("div", { className: "ac-bar-row", role: "listitem", children: [_jsx("span", { className: "ac-bar-label", title: String(row[0] ?? ''), children: String(row[0] ?? '') }), _jsx("span", { className: "ac-bar-tracks", children: block.series.map((s, j) => {
                        const value = num(row[j + 1]);
                        const left = ((Math.min(value, 0) - min) / span) * 100;
                        const width = (Math.abs(value) / span) * 100;
                        return (_jsxs("span", { className: "ac-bar-track", children: [_jsx("span", { className: "ac-bar", style: { left: `${left}%`, width: `${Math.max(width, 0.5)}%`, background: color(j) } }), _jsx("span", { className: "ac-bar-value", children: formatValue(row[j + 1], s.format, block.currency) })] }, s.key));
                    }) })] }, i))) }));
}
const W = 320;
const H = 170;
const PAD = { left: 46, right: 10, top: 10, bottom: 26 };
function LineChart({ block }) {
    const values = block.rows.flatMap((row) => row.slice(1).map(num));
    const max = Math.max(...values, 0);
    const min = Math.min(...values, 0);
    const span = max - min || 1;
    const n = block.rows.length;
    const x = (i) => PAD.left + (n === 1 ? 0 : (i / (n - 1)) * (W - PAD.left - PAD.right));
    const y = (v) => PAD.top + (1 - (v - min) / span) * (H - PAD.top - PAD.bottom);
    const format = block.series[0]?.format;
    const ticks = [max, (max + min) / 2, min];
    const labelAt = n <= 6 ? block.rows.map((_, i) => i) : [0, Math.floor((n - 1) / 2), n - 1];
    return (_jsxs("svg", { className: "ac-line", viewBox: `0 0 ${W} ${H}`, role: "img", "aria-label": block.title, children: [ticks.map((t, i) => (_jsxs("g", { children: [_jsx("line", { x1: PAD.left, x2: W - PAD.right, y1: y(t), y2: y(t), className: "ac-grid" }), _jsx("text", { x: PAD.left - 6, y: y(t) + 3, textAnchor: "end", className: "ac-axis", children: compact(t, format, block.currency) })] }, i))), labelAt.map((i) => (_jsx("text", { x: x(i), y: H - 8, textAnchor: i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle', className: "ac-axis", children: String(block.rows[i][0] ?? '') }, i))), block.series.map((s, j) => (_jsxs("g", { children: [_jsx("polyline", { fill: "none", stroke: color(j), strokeWidth: 2, strokeLinejoin: "round", points: block.rows.map((row, i) => `${x(i)},${y(num(row[j + 1]))}`).join(' ') }), block.rows.map((row, i) => (_jsx("circle", { cx: x(i), cy: y(num(row[j + 1])), r: n > 24 ? 1.5 : 3, fill: color(j), children: _jsx("title", { children: `${String(row[0] ?? '')} · ${s.label}: ${formatValue(row[j + 1], s.format, block.currency)}` }) }, i)))] }, s.key)))] }));
}
function PieChart({ block }) {
    const s = block.series[0];
    const values = block.rows.map((row) => Math.max(0, num(row[1])));
    const total = values.reduce((a, b) => a + b, 0) || 1;
    let at = 0;
    const stops = values.map((v, i) => {
        const from = (at / total) * 360;
        at += v;
        return `${color(i)} ${from}deg ${(at / total) * 360}deg`;
    });
    return (_jsxs("div", { className: "ac-pie", children: [_jsx("div", { className: "ac-donut", style: { background: `conic-gradient(${stops.join(', ')})` }, "aria-hidden": "true" }), _jsx("ul", { className: "ac-pie-legend", children: block.rows.map((row, i) => (_jsxs("li", { children: [_jsx("i", { style: { background: color(i) } }), _jsx("span", { className: "ac-pie-name", children: String(row[0] ?? '') }), _jsxs("span", { className: "ac-pie-value", children: [formatValue(row[1], s?.format, block.currency), " \u00B7 ", Math.round((values[i] / total) * 100), "%"] })] }, i))) })] }));
}
